import { z } from 'zod';
import { AUDIT_REASON_MIN_LENGTH } from './audit-reasons.ts';

/**
 * The partner payout run — the shapes, for both domains.
 *
 * ── WHY ONE FILE FOR TWO INDEPENDENT DOMAINS ───────────────────────────────
 * The agent and affiliate domains are structurally independent and stay that
 * way: two engines, two rule sets, two period tables, two route sets. What is
 * shared here is not a RULE, it is a FORM — "why", "who", "what reference" —
 * and it is shared for the same reason `@dayon/money` is shared: two copies of
 * a validation message diverge, and the divergence is discovered by an operator
 * who is told two different things about the same field on two screens.
 *
 * Nothing here decides who may be paid, how much, or on what cadence. Those are
 * in each domain's own engine and route, where they differ.
 *
 * ── MAKER AND CHECKER ──────────────────────────────────────────────────────
 * `agent_payout_maker_is_not_checker` and `affiliate_payout_maker_is_not_checker`
 * are real CHECK constraints, so the rule holds whatever writes the row. The
 * routes refuse FIRST anyway, naming the rule, because an operator who is shown
 * a constraint name has been told that something is broken rather than that
 * somebody else has to approve this.
 */

/**
 * A reason an operator has to write, floored where the database floors it.
 *
 * It said eight characters until item 41 landed, and the database says ten. The
 * handler writes the operator's own sentence into the audit row unchanged — which
 * is the right thing to do, since the sentence IS the reason — so a nine-character
 * reason passed this check, reached `platform.audit_log`, and came back as
 * **409 "That change is not allowed by the platform rules."** An operator one
 * character short was told the platform forbids what they were doing.
 *
 * `AUDIT_REASON_MIN_LENGTH` is the database's own floor, so the two cannot drift
 * again, and the message carries the number rather than restating it.
 */
const reason = z
  .string()
  .trim()
  .min(
    AUDIT_REASON_MIN_LENGTH,
    `Give a reason of at least ${AUDIT_REASON_MIN_LENGTH} characters — it is the audit trail.`,
  )
  .max(500);

const note = z.string().trim().max(500).optional();

/**
 * Schedule the payout run for one settled period.
 *
 * There is deliberately no list of agents or amounts here. The run is derived
 * from the period's OWN results — a caller who could name the amount could pay
 * a partner a figure the statement does not carry.
 */
export const schedulePayoutRunRequest = z.object({ reason });
export type SchedulePayoutRunRequest = z.infer<typeof schedulePayoutRunRequest>;

/** The checker's approval. A note, not a reason: approving is agreeing. */
export const approvePayoutRequest = z.object({ note });
export type ApprovePayoutRequest = z.infer<typeof approvePayoutRequest>;

/** Refusing to pay is a decision, so it carries a reason. */
export const rejectPayoutRequest = z.object({ reason });
export type RejectPayoutRequest = z.infer<typeof rejectPayoutRequest>;

/**
 * Record that an approved payout has been settled.
 *
 * `reference` is the transfer's reference AT THE BANK OR WALLET, because this
 * platform does not move the money: no payment provider is integrated and none
 * is stubbed. This route records a settlement a person made outside it, which
 * is why the reference is required — without it the row asserts a payment with
 * nothing behind it, and there would be no way to reconcile the statement
 * against what actually left the account.
 */
export const recordPayoutSettledRequest = z.object({
  reference: z
    .string()
    .trim()
    .min(3, 'Record the bank or wallet reference for the transfer that was actually made.')
    .max(80),
  note,
});
export type RecordPayoutSettledRequest = z.infer<typeof recordPayoutSettledRequest>;
